import React, { useState, useEffect } from 'react';
import { api, setStation } from '../api';

const FORMATS = ['Top 40','R&B / Soul','Hip-Hop','Gospel / Christian','Jazz / Blues','Country','Rock','Latin / Reggaeton','Afrobeats','News / Talk','Sports','Classical'];
const MODES   = ['azuracast','liquidsoap','sftp','groovie_hosted'];
const MODE_LABELS = {
  azuracast:      'AzuraCast (Native API)',
  liquidsoap:     'Liquidsoap (Relay)',
  sftp:           'SFTP Drop-in',
  groovie_hosted: 'Groovie-Hosted — Coming soon (join waitlist)',
};

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:500, maxHeight:'90vh', overflowY:'auto', position:'relative' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
          <h3 style={{ margin:0, fontSize:17, fontWeight:700, color:'#0f172a' }}>{title}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#64748b', lineHeight:1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Stations() {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [provStation, setProvStation] = useState(null);   // station being provisioned (picker)
  const [provList, setProvList]       = useState([]);      // candidate AzuraCast stations
  const [provBusy, setProvBusy]       = useState(null);    // station id currently provisioning
  const [form, setForm] = useState({
    callsign:'', city:'', state:'', market:'', format:'', integration_mode:'azuracast',
    azuracast_url:'', azuracast_api_key:'', active_hours_start:'22:00', active_hours_end:'06:00'
  });
  const set = (k,v) => setForm(p => ({...p,[k]:v}));

  const load = () => {
    api.get('/broadcast/stations')
      .then(d => setStations(d.stations || []))
      .catch(() => setStations([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const s = await api.post('/broadcast/stations', form);
      setStation(s);
      setShowAdd(false);
      load();
    } catch(err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const selectStation = (s) => { setStation(s); window.location.reload(); };

  // Configure = provision. BYO (azuracast) validates the broadcaster's own box and either
  // auto-activates (one station) or opens a picker (multiple). groovie_hosted/liquidsoap/
  // sftp return 501/503 which we surface verbatim.
  const configure = async (s) => {
    setProvBusy(s.id); setError('');
    try {
      const r = await api.post(`/broadcast/stations/${s.id}/provision`, {});
      if (r.status === 'active') {
        alert(`✓ ${s.callsign} is now active (AzuraCast station ${r.azuracast_station_id}).`);
        load();
      } else if (r.status === 'provisioning' && (r.stations || []).length) {
        setProvStation(s); setProvList(r.stations);          // open picker
      } else {
        alert(r.message || 'Provisioning started — finish by selecting a station.');
        load();
      }
    } catch (err) { alert(err.message); }
    finally { setProvBusy(null); }
  };

  const pickStation = async (azId) => {
    if (!provStation) return;
    setProvBusy(provStation.id);
    try {
      const r = await api.post(`/broadcast/stations/${provStation.id}/provision/select`, { azuracast_station_id: azId });
      alert(`✓ ${provStation.callsign} bound to station ${r.azuracast_station_id}${r.station_name ? ' ('+r.station_name+')' : ''}.`);
      setProvStation(null); setProvList([]); load();
    } catch (err) { alert(err.message); }
    finally { setProvBusy(null); }
  };

  const inp = (k, label, type='text', placeholder='') => (
    <div>
      <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>{label}</label>
      <input type={type} value={form[k]} onChange={e => set(k, e.target.value)}
        placeholder={placeholder}
        style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
        onFocus={e => e.target.style.borderColor='#6366f1'}
        onBlur={e => e.target.style.borderColor='#e2e8f0'}
      />
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <p style={{ margin:0, fontSize:13, color:'#64748b' }}>Manage your radio stations on the Groovie Broadcast network</p>
        </div>
        <button onClick={() => setShowAdd(true)} style={{ background:'linear-gradient(135deg,#6366f1,#4f46e5)', color:'#fff', border:'none', borderRadius:8, padding:'9px 18px', fontSize:13, fontWeight:700, cursor:'pointer' }}>
          + Add Station
        </button>
      </div>

      {loading ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:16 }}>
          {[1,2].map(i => <div key={i} style={{ height:160, background:'#f1f5f9', borderRadius:12 }} />)}
        </div>
      ) : stations.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 20px', background:'#fff', borderRadius:16, border:'2px dashed #e2e8f0' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>📡</div>
          <h3 style={{ margin:'0 0 8px', color:'#0f172a' }}>No stations yet</h3>
          <p style={{ color:'#64748b', fontSize:14, margin:'0 0 20px' }}>Add your first radio station to start using Groovie Broadcast AI products.</p>
          <button onClick={() => setShowAdd(true)} style={{ background:'linear-gradient(135deg,#6366f1,#4f46e5)', color:'#fff', border:'none', borderRadius:8, padding:'10px 24px', fontSize:14, fontWeight:700, cursor:'pointer' }}>
            Register Your Station
          </button>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:16 }}>
          {stations.map(s => (
            <div key={s.id} style={{ background:'#fff', borderRadius:12, padding:20, border:'1px solid #f1f5f9', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
                <div>
                  <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:'#0f172a' }}>{s.callsign}</h3>
                  <p style={{ margin:'2px 0 0', fontSize:12, color:'#64748b' }}>{s.city}, {s.state} · {s.format}</p>
                </div>
                <span style={{ fontSize:11, fontWeight:600, padding:'4px 10px', borderRadius:20, background: s.status==='active' ? '#f0fdf4' : '#fafafa', color: s.status==='active' ? '#15803d' : '#64748b', border: `1px solid ${s.status==='active'?'#bbf7d0':'#e2e8f0'}` }}>
                  {s.status || 'registered'}
                </span>
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
                <span style={{ fontSize:11, padding:'3px 8px', borderRadius:6, background:'#eef2ff', color:'#4f46e5', fontWeight:600 }}>{s.integration_mode || 'azuracast'}</span>
                <span style={{ fontSize:11, padding:'3px 8px', borderRadius:6, background:'#f0fdf4', color:'#15803d', fontWeight:600 }}>{s.market || s.city}</span>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => selectStation(s)} style={{ flex:1, padding:'8px', borderRadius:8, border:'1.5px solid #6366f1', background:'#fff', color:'#6366f1', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                  Set Active
                </button>
                <button onClick={() => configure(s)} disabled={provBusy===s.id} style={{ flex:1, padding:'8px', borderRadius:8, border:'1.5px solid #e2e8f0', background:'#fff', color:'#374151', fontSize:12, fontWeight:600, cursor: provBusy===s.id?'not-allowed':'pointer' }}>
                  {provBusy===s.id ? 'Provisioning…' : (s.azuracast_station_id ? 'Reconfigure' : 'Configure')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <Modal title="Register New Station" onClose={() => setShowAdd(false)}>
          {error && <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'10px 14px', marginBottom:14, color:'#dc2626', fontSize:13 }}>{error}</div>}
          <form onSubmit={handleAdd} style={{ display:'flex', flexDirection:'column', gap:13 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {inp('callsign','Call Sign / Station Name','text','WKRS 97.5')}
              {inp('city','City','text','Memphis')}
              {inp('state','State / Region','text','TN')}
              {inp('market','Market Name','text','Memphis Metro')}
            </div>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Station Format</label>
              <select value={form.format} onChange={e => set('format',e.target.value)} required style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none' }}>
                <option value="">Select format…</option>
                {FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Integration Mode</label>
              <select value={form.integration_mode} onChange={e => set('integration_mode',e.target.value)} style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none' }}>
                {MODES.map(m => <option key={m} value={m} disabled={m === 'groovie_hosted'}>{MODE_LABELS[m]}</option>)}
              </select>
            </div>
            {form.integration_mode === 'azuracast' && (
              <>
                {inp('azuracast_url','AzuraCast URL','text','https://radio.yourdomain.com')}
                {inp('azuracast_api_key','AzuraCast API Key','password','')}
              </>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {inp('active_hours_start','AI Host Start Time','time','')}
              {inp('active_hours_end','AI Host End Time','time','')}
            </div>
            <div style={{ display:'flex', gap:10, marginTop:4 }}>
              <button type="button" onClick={() => setShowAdd(false)} style={{ flex:1, padding:10, borderRadius:8, border:'1.5px solid #e2e8f0', background:'#fff', color:'#374151', fontWeight:600, fontSize:13, cursor:'pointer' }}>Cancel</button>
              <button type="submit" disabled={saving} style={{ flex:2, padding:10, borderRadius:8, border:'none', background: saving?'#c7d2fe':'linear-gradient(135deg,#6366f1,#4f46e5)', color:'#fff', fontWeight:700, fontSize:13, cursor: saving?'not-allowed':'pointer' }}>
                {saving ? 'Registering…' : 'Register Station'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {provStation && (
        <Modal title={`Select AzuraCast station for ${provStation.callsign}`} onClose={() => { setProvStation(null); setProvList([]); }}>
          <p style={{ margin:'0 0 14px', fontSize:13, color:'#64748b' }}>We found multiple stations on your AzuraCast server. Choose the one to power {provStation.callsign}.</p>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {provList.map(st => (
              <button key={st.id} onClick={() => pickStation(st.id)} disabled={provBusy===provStation.id}
                style={{ textAlign:'left', padding:'10px 14px', borderRadius:8, border:'1.5px solid #e2e8f0', background:'#fff', cursor: provBusy===provStation.id?'not-allowed':'pointer' }}>
                <div style={{ fontSize:13, fontWeight:700, color:'#0f172a' }}>{st.name || st.shortcode || `Station ${st.id}`}</div>
                <div style={{ fontSize:11, color:'#94a3b8' }}>id {st.id}{st.shortcode ? ` · ${st.shortcode}` : ''}</div>
              </button>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}
